import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

interface SelectContextValue {
  value: string;
  onValueChange: (value: string) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLButtonElement>;
  contentId: string;
}

const SelectContext = createContext<SelectContextValue>({
  value: '',
  onValueChange: () => {},
  open: false,
  setOpen: () => {},
  triggerRef: { current: null },
  contentId: '',
});

interface SelectProps {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  children: React.ReactNode;
  disabled?: boolean;
}

let _selectIdCounter = 0;

export const Select: React.FC<SelectProps> = ({ value: controlledValue, defaultValue = '', onValueChange, children, disabled }) => {
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [contentId] = useState(() => `select-content-${++_selectIdCounter}`);
  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : uncontrolledValue;

  const handleChange = (v: string) => {
    if (!isControlled) setUncontrolledValue(v);
    onValueChange?.(v);
    setOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (triggerRef.current && !triggerRef.current.closest('[data-select-root]')?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <SelectContext.Provider value={{ value, onValueChange: handleChange, open, setOpen, triggerRef, contentId }}>
      <div data-select-root className="relative inline-block w-full" data-disabled={disabled || undefined}>
        {children}
      </div>
    </SelectContext.Provider>
  );
};

interface SelectTriggerProps {
  children: React.ReactNode;
  className?: string;
  'data-testid'?: string;
}

export const SelectTrigger: React.FC<SelectTriggerProps> = ({ children, className = '', 'data-testid': testId }) => {
  const { open, setOpen, triggerRef, contentId } = useContext(SelectContext);

  return (
    <button
      ref={triggerRef}
      type="button"
      role="combobox"
      aria-controls={contentId}
      aria-expanded={open}
      data-testid={testId}
      onClick={() => setOpen(!open)}
      className={[
        'flex w-full items-center justify-between rounded-md border border-border bg-white px-3 py-2',
        'text-sm text-text-primary',
        'focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        'transition-colors',
        className,
      ].join(' ')}
    >
      {children}
      <ChevronDown size={18} className={`text-text-secondary transition-transform ${open ? 'rotate-180' : ''}`} />
    </button>
  );
};

interface SelectValueProps {
  placeholder?: string;
  className?: string;
  children?: React.ReactNode;
}

export const SelectValue: React.FC<SelectValueProps> = ({ placeholder, className = '', children }) => {
  const { value } = useContext(SelectContext);
  return (
    <span className={`block truncate ${!value ? 'text-text-secondary' : ''} ${className}`}>
      {children || value || placeholder}
    </span>
  );
};

interface SelectContentProps {
  children: React.ReactNode;
  className?: string;
}

export const SelectContent: React.FC<SelectContentProps> = ({ children, className = '' }) => {
  const { open, contentId } = useContext(SelectContext);
  if (!open) return null;

  return (
    <div
      id={contentId}
      data-radix-select-content
      className={[
        'absolute top-full left-0 z-50 mt-1 w-full min-w-[8rem]',
        'bg-white rounded-md border border-border shadow-lg',
        'py-1 overflow-auto max-h-60',
        className,
      ].join(' ')}
    >
      {children}
    </div>
  );
};

interface SelectItemProps {
  value: string;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
}

export const SelectItem: React.FC<SelectItemProps> = ({ value, children, className = '', disabled }) => {
  const { value: selectedValue, onValueChange } = useContext(SelectContext);
  const isSelected = selectedValue === value;

  return (
    <div
      role="option"
      aria-selected={isSelected}
      onClick={() => !disabled && onValueChange(value)}
      className={[
        'relative flex items-center px-3 py-2 text-sm cursor-pointer select-none',
        'hover:bg-primary/5 hover:text-primary',
        isSelected ? 'bg-primary/10 text-primary font-medium' : 'text-text-primary',
        disabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : '',
        className,
      ].join(' ')}
    >
      {children}
    </div>
  );
};

export const SelectLabel: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`px-3 py-1.5 text-xs font-semibold text-text-secondary ${className}`}>
    {children}
  </div>
);

export const SelectSeparator: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`my-1 h-px bg-border ${className}`} />
);
